import type { PlaybookSchema, PlaybookSection, PlaybookQuestion } from '@doneverse/shared'
import type { SchemaField } from './schema-field.js'

/**
 * Transforms a nested PlaybookSchema (backend) into a flat SchemaField array (frontend).
 */
export function flattenSchema(playbookSchema: PlaybookSchema): SchemaField[] {
  const flatFields: SchemaField[] = []

  playbookSchema.forEach((section) => {
    flatFields.push({
      id: section.id,
      type: 'section',
      title: section.title,
      description: section.description,
    })

    section.questions.forEach((question) => {
      flatFields.push({
        id: question.id,
        question: question.question,
        description: question.description,
        field_type: question.fieldType,
        displayAs: question.displayAs,
        options: question.options,
        optionImages: question.optionImages,
        required: question.required ?? true,
        showWhen: question.showWhen,
      })
    })
  })

  return flatFields
}

/**
 * Transforms a flat SchemaField array (frontend) into a nested PlaybookSchema (backend).
 */
export function nestSchema(flatFields: SchemaField[]): PlaybookSchema {
  const sections: PlaybookSection[] = []
  let currentSection: PlaybookSection | null = null

  flatFields.forEach((field) => {
    if (field.type === 'section') {
      currentSection = {
        id: String(field.id),
        title: field.title || 'Untitled Section',
        description: field.description,
        questions: [],
      }
      sections.push(currentSection)
    } else {
      if (!currentSection) {
        currentSection = {
          id: `section-${Date.now()}`,
          title: 'General',
          questions: [],
        }
        sections.push(currentSection)
      }

      const question: PlaybookQuestion = {
        id: String(field.id),
        number: String(currentSection.questions.length + 1),
        question: field.question || 'Untitled Question',
        description: field.description,
        fieldType: (field.field_type || 'short_text') as PlaybookQuestion['fieldType'],
        displayAs: field.displayAs,
        options: field.options,
        optionImages: field.optionImages,
        required: field.required,
        showWhen: field.showWhen,
      }

      currentSection.questions.push(question)
    }
  })

  return sections
}
